import useUserStore from 'common/src/stores/useUserStore';
import { WEEK_CHART, ARRIVAL_CHART, STATUS_COLORS } from './mock';

// Colores de soporte de los SVG, sensibles al tema (los colores de datos son fijos).
const useChartInk = () => {
  const { darkMode } = useUserStore();
  return darkMode
    ? { ink: '#ECEDEE', ink2: '#A1A1AA', ink3: '#8A8A93', grid: '#2E2E33', axis: '#3F3F46', vol: '#2E2E33', volHi: '#3F3F46', band: '#3F3F46', area: '#26262B' }
    : { ink: '#11181C', ink2: '#52525B', ink3: '#A1A1AA', grid: '#F4F4F5', axis: '#E4E4E7', vol: '#F4F4F5', volHi: '#E4E4E7', band: '#E4E4E7', area: '#F4F4F5' };
};

const MONO = 'inherit';

/** Índice de frescura por semana de embalaje: tu línea vs. banda P25–P75. */
export const WeekIndexChart = () => {
  const c = useChartInk();
  const xs = WEEK_CHART.weeks.map((_, i) => 50 + (820 / (WEEK_CHART.weeks.length - 1)) * i);
  const hiIdx = WEEK_CHART.weeks.indexOf(WEEK_CHART.highlightWeek);

  const bandPoints = [
    ...xs.map((x, i) => `${x},${WEEK_CHART.bandTop[i]}`),
    ...[...xs].reverse().map((x, i) => `${x},${[...WEEK_CHART.bandBottom].reverse()[i]}`),
  ].join(' ');

  return (
    <svg className='w-full h-auto block' viewBox='0 0 900 272' role='img' aria-label='Índice de frescura por semana de embalaje'>
      {xs.map((x, i) => (
        <rect key={i} x={x - 15} y={230 - WEEK_CHART.volHeights[i]} width='30' height={WEEK_CHART.volHeights[i]} fill={i === hiIdx || i === hiIdx - 1 ? c.volHi : c.vol} />
      ))}
      <text x={xs[hiIdx]} y={185} textAnchor='middle' fontFamily={MONO} fontSize='10.5' fill={c.ink3}>{WEEK_CHART.volLabel}</text>

      <line x1='50' y1='30' x2='870' y2='30' stroke={c.grid} />
      <line x1='50' y1='136.67' x2='870' y2='136.67' stroke={c.grid} />
      <line x1='50' y1='190' x2='870' y2='190' stroke={c.grid} />
      <text x='42' y='34' textAnchor='end' fontFamily={MONO} fontSize='10.5' fill={c.ink3}>1,20</text>
      <text x='42' y='140.7' textAnchor='end' fontFamily={MONO} fontSize='10.5' fill={c.ink3}>0,80</text>
      <text x='42' y='194' textAnchor='end' fontFamily={MONO} fontSize='10.5' fill={c.ink3}>0,60</text>

      <polygon fill={c.band} fillOpacity='0.75' points={bandPoints} />

      <line x1='50' y1='83.33' x2='870' y2='83.33' stroke={STATUS_COLORS.critico} strokeWidth='1' strokeDasharray='4 3' />
      <text x='54' y='79' fontFamily={MONO} fontSize='10.5' fill={STATUS_COLORS.critico}>1,00 — vencido</text>

      <polyline fill='none' stroke='#BE123C' strokeWidth='2.2' strokeLinejoin='round' points={xs.map((x, i) => `${x},${WEEK_CHART.yourLine[i]}`).join(' ')} />
      {xs.map((x, i) =>
        i === hiIdx ? (
          <circle key={i} cx={x} cy={WEEK_CHART.yourLine[i]} r='4.5' fill={STATUS_COLORS.holgado} stroke='#fff' strokeWidth='1.5' />
        ) : (
          <circle key={i} cx={x} cy={WEEK_CHART.yourLine[i]} r='3.5' fill='#BE123C' />
        )
      )}
      <text x={xs[hiIdx]} y={WEEK_CHART.yourLine[hiIdx] + 17} textAnchor='middle' fontFamily={MONO} fontSize='10.5' fill={STATUS_COLORS.holgado}>{WEEK_CHART.highlightLabel}</text>

      <line x1='50' y1='230' x2='870' y2='230' stroke={c.axis} />
      {xs.map((x, i) => (
        <text key={i} x={x} y='248' textAnchor='middle' fontFamily={MONO} fontSize='11' fill={i === hiIdx ? c.ink : c.ink2} fontWeight={i === hiIdx ? 600 : 400}>
          {WEEK_CHART.weeks[i]}
        </text>
      ))}
      <text x='460' y='266' textAnchor='middle' fontSize='10' letterSpacing='1.4' fill={c.ink3}>SEMANA DE EMBALAJE</text>
    </svg>
  );
};

/** Arribo → desconsolidación por semana de arribo: desviación vs. mediana + franja de días absolutos. */
export const ArrivalDeviationChart = () => {
  const c = useChartInk();
  const n = ARRIVAL_CHART.weeks.length;
  const xs = ARRIVAL_CHART.weeks.map((_, i) => 55 + (815 / (n - 1)) * i);

  const areaPoints = [
    ...xs.map((x, i) => `${x},${ARRIVAL_CHART.industryArrivals[i]}`),
    `${xs[n - 1]},230`,
    `${xs[0]},230`,
  ].join(' ');

  return (
    <svg className='w-full h-auto block' viewBox='0 0 900 330' role='img' aria-label='Desviación del tramo arribo a desconsolidación por semana de arribo'>
      <polygon fill={c.area} points={areaPoints} />
      <text x={xs[3]} y='180' textAnchor='middle' fontFamily={MONO} fontSize='10.5' fill={c.ink3}>{ARRIVAL_CHART.arrivalsLabel}</text>

      <line x1='55' y1='40' x2='870' y2='40' stroke={c.grid} />
      <line x1='55' y1='180' x2='870' y2='180' stroke={c.grid} />
      <text x='47' y='44' textAnchor='end' fontFamily={MONO} fontSize='10.5' fill={STATUS_COLORS.leve}>+1,5 d</text>
      <text x='47' y='184' textAnchor='end' fontFamily={MONO} fontSize='10.5' fill={STATUS_COLORS.holgado}>−1,5 d</text>
      <text x='47' y='114' textAnchor='end' fontFamily={MONO} fontSize='10.5' fill={c.ink2}>0</text>

      {xs.map((x, i) => {
        const b = ARRIVAL_CHART.bars[i];
        return b.up ? (
          <rect key={i} x={x - 17} y={110 - b.h} width='34' height={b.h} fill={STATUS_COLORS.leve} />
        ) : (
          <rect key={i} x={x - 17} y={110} width='34' height={b.h} fill={STATUS_COLORS.holgado} />
        );
      })}

      <line x1='55' y1='110' x2='870' y2='110' stroke={c.ink} strokeWidth='1.5' />
      <text x='60' y='106' fontSize='10' letterSpacing='1.2' fill={c.ink}>MEDIANA INDUSTRIA</text>

      <line x1={xs[ARRIVAL_CHART.cnyIndex]} y1='34' x2={xs[ARRIVAL_CHART.cnyIndex]} y2='230' stroke={STATUS_COLORS.critico} strokeWidth='1' strokeDasharray='3 3' />
      <text x={xs[ARRIVAL_CHART.cnyIndex] - 5} y='32' textAnchor='end' fontFamily={MONO} fontSize='10.5' fill={STATUS_COLORS.critico}>Año Nuevo chino</text>

      <line x1='55' y1='230' x2='870' y2='230' stroke={c.axis} />
      {xs.map((x, i) => (
        <text key={i} x={x} y='248' textAnchor='middle' fontFamily={MONO} fontSize='11' fill={c.ink2}>{ARRIVAL_CHART.weeks[i]}</text>
      ))}

      <line x1='55' y1='258' x2='870' y2='258' stroke={c.axis} />
      <line x1='55' y1='284' x2='870' y2='284' stroke={c.grid} />
      <line x1='55' y1='310' x2='870' y2='310' stroke={c.axis} />
      <text x='47' y='277' textAnchor='end' fontSize='9.5' letterSpacing='1' fill={c.ink} fontWeight='600'>TÚ</text>
      <text x='47' y='303' textAnchor='end' fontSize='9.5' letterSpacing='1' fill={c.ink3} fontWeight='600'>IND.</text>
      {xs.map((x, i) => (
        <text
          key={i}
          x={x}
          y='277'
          textAnchor='middle'
          fontFamily={MONO}
          fontSize='11'
          fill={i === ARRIVAL_CHART.youWorst ? STATUS_COLORS.critico : i === ARRIVAL_CHART.youBest ? STATUS_COLORS.holgado : c.ink}
          fontWeight={i === ARRIVAL_CHART.youWorst || i === ARRIVAL_CHART.youBest ? 600 : 400}
        >
          {ARRIVAL_CHART.you[i]}
        </text>
      ))}
      {xs.map((x, i) => (
        <text key={i} x={x} y='303' textAnchor='middle' fontFamily={MONO} fontSize='11' fill={c.ink3}>{ARRIVAL_CHART.industry[i]}</text>
      ))}
      <text x='462' y='324' textAnchor='middle' fontSize='10' letterSpacing='1.4' fill={c.ink3}>SEMANA DE ARRIBO · DÍAS ARRIBO → DESCONSOLIDACIÓN</text>
    </svg>
  );
};
